/**
 * @fullvsl/contracts/analytics-schema — Analytics event schema (FUL-6).
 *
 * Formalizes, as a typed contract extending FUL-5 (§7):
 *  §A  The full analytics event vocabulary — a self-describing registry mapping
 *      every `AnalyticsEventType` to its definition (when it fires, required
 *      meta, dedupe class, retention relevance). FUL-9 (CTA reveal), FUL-15
 *      (event adapter) and FUL-17 (dashboard) consume this registry instead of
 *      re-deriving semantics.
 *  §B  Idempotency / dedupe key strategy — two classes (`event_id`,
 *      `natural_key`) plus a pure `eventNaturalKey()` derivation. This is what
 *      makes the event APIs resilient to repeated AND late events even when a
 *      retrying player regenerates `eventId` (PRD §10 + backend-engineer
 *      constraint: "resilient to repeated/late events").
 *  §C  Retention milestone-bucket calculation rules — the canonical bucket
 *      grids (deciles + milestones), a pure `bucketizePct()`, and
 *      `computeRetentionByBuckets()` so the dashboard retention view is defined
 *      once, on max-watched progress (anti-gaming, matches CTA `basedOn`).
 *  §D  Conversion proxy — the canonical event + the conversion-rate rule.
 *
 * Additive-only over contracts.ts: no existing type is narrowed or removed.
 * Stable + additive; breaking changes require a contracts revision + CTO review.
 */

import type { AnalyticsEvent, AnalyticsEventType, ID } from "./contracts";

/* =====================================================================================
 * §A. Full event vocabulary
 * ===================================================================================== */

/** Logical funnel an event belongs to (drives aggregation + Full Funnel mapping). */
export type EventCategory =
  | "funnel_top" // impression, autoplay_impression
  | "engagement" // play, pause, progress, seek, milestone, completed
  | "cta" // cta_shown, cta_clicked (cta_clicked = conversion proxy)
  | "resume" // resume_offered, resume_accepted, resume_declined
  | "overlay" // pause_overlay_shown, pause_overlay_action
  | "offer" // offer_moment_reached
  | "system"; // error

/** How the server dedupes this event type (see §B). */
export type EventDedupeClass = "event_id" | "natural_key";

/**
 * Canonical definition of one analytics event type.
 *
 * `requiredMeta` lists meta keys the ingest layer MUST enforce for that type
 * (validation rejects events missing them, without aborting the batch — see
 * ingest.ts). Optional meta stays free-form via `AnalyticsEventMeta[k]`.
 */
export interface AnalyticsEventSpec {
  type: AnalyticsEventType;
  category: EventCategory;
  /** Short human description of when this event fires. */
  firesWhen: string;
  dedupe: EventDedupeClass;
  /** Meta keys required for this event type (empty = none required). */
  requiredMeta: string[];
  /** True when this event contributes a max-watched pct to retention. */
  retentionRelevant: boolean;
}

/**
 * The full, typed event vocabulary. `Record<AnalyticsEventType, …>` makes the
 * registry exhaustive: adding a new `AnalyticsEventType` in contracts.ts is a
 * compile error here until its spec is added. Consumers (FUL-9/15/17) look up
 * semantics from this single source of truth.
 */
export const ANALYTICS_EVENT_VOCABULARY: Record<AnalyticsEventType, AnalyticsEventSpec> = {
  impression: {
    type: "impression",
    category: "funnel_top",
    firesWhen: "Player mounted / VSL first rendered to a visitor (once per session).",
    dedupe: "natural_key",
    requiredMeta: [],
    retentionRelevant: false,
  },
  autoplay_impression: {
    type: "autoplay_impression",
    category: "funnel_top",
    firesWhen: "Autoplay attempt fired (tracked separately from impression when configured).",
    dedupe: "natural_key",
    requiredMeta: [],
    retentionRelevant: false,
  },
  play: {
    type: "play",
    category: "engagement",
    firesWhen: "Playback started or resumed by the visitor (may repeat after pauses).",
    dedupe: "event_id",
    requiredMeta: ["positionSec"],
    retentionRelevant: false,
  },
  pause: {
    type: "pause",
    category: "engagement",
    firesWhen: "Playback paused by the visitor.",
    dedupe: "event_id",
    requiredMeta: ["positionSec"],
    retentionRelevant: false,
  },
  progress: {
    type: "progress",
    category: "engagement",
    firesWhen: "Throttled heartbeat (≥1s AND ≥5% step — see PROGRESS_THROTTLE).",
    dedupe: "event_id",
    requiredMeta: ["pct"],
    retentionRelevant: true,
  },
  milestone: {
    type: "milestone",
    category: "engagement",
    firesWhen: "Max-watched progress first crosses 25/50/75/100 (maxWatched, once each).",
    dedupe: "natural_key",
    requiredMeta: ["milestone", "pct"],
    retentionRelevant: true,
  },
  completed: {
    type: "completed",
    category: "engagement",
    firesWhen: "Playback reached the end (100%).",
    dedupe: "natural_key",
    requiredMeta: [],
    retentionRelevant: true,
  },
  seek: {
    type: "seek",
    category: "engagement",
    firesWhen: "Visitor scrubbed from one position to another.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  cta_shown: {
    type: "cta_shown",
    category: "cta",
    firesWhen: "CTA revealed (once per session — reveal is monotonic by default).",
    dedupe: "natural_key",
    requiredMeta: [],
    retentionRelevant: false,
  },
  cta_clicked: {
    type: "cta_clicked",
    category: "cta",
    firesWhen: "Visitor clicked the CTA (conversion proxy — may repeat per click).",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  resume_offered: {
    type: "resume_offered",
    category: "resume",
    firesWhen: "Resume prompt shown to a returning visitor.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  resume_accepted: {
    type: "resume_accepted",
    category: "resume",
    firesWhen: "Visitor accepted the resume prompt.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  resume_declined: {
    type: "resume_declined",
    category: "resume",
    firesWhen: "Visitor declined the resume prompt.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  pause_overlay_shown: {
    type: "pause_overlay_shown",
    category: "overlay",
    firesWhen: "A pause overlay was displayed.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  pause_overlay_action: {
    type: "pause_overlay_action",
    category: "overlay",
    firesWhen: "Visitor interacted with a pause overlay.",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
  offer_moment_reached: {
    type: "offer_moment_reached",
    category: "offer",
    firesWhen: "Organic viewing reached the configured offer moment (once per session).",
    dedupe: "natural_key",
    requiredMeta: [],
    retentionRelevant: false,
  },
  error: {
    type: "error",
    category: "system",
    firesWhen: "Player/media error (recovered or fatal).",
    dedupe: "event_id",
    requiredMeta: [],
    retentionRelevant: false,
  },
};

/** Event types grouped by category (convenience for dashboards/adapters). */
export const ANALYTICS_EVENTS_BY_CATEGORY: Record<EventCategory, AnalyticsEventType[]> = (
  Object.values(ANALYTICS_EVENT_VOCABULARY) as AnalyticsEventSpec[]
).reduce<Record<EventCategory, AnalyticsEventType[]>>((acc, spec) => {
  (acc[spec.category] ??= []).push(spec.type);
  return acc;
}, {
  funnel_top: [],
  engagement: [],
  cta: [],
  resume: [],
  overlay: [],
  offer: [],
  system: [],
});

/* =====================================================================================
 * §B. Idempotency / dedupe key strategy
 *
 * Two dedupe classes, layered:
 *
 *  1. `event_id` (default, every event): the client stamps a UUID v4 `eventId`.
 *     The server dedupes by `eventId`, so an exact retry (same payload) is a
 *     no-op. This is the FUL-5 path and stays the baseline idempotency key.
 *
 *  2. `natural_key` (unique-per-session events): for events that should occur at
 *     most once per logical occurrence in a session (impression, autoplay
 *     impression, each milestone 25/50/75/100, completed, cta_shown,
 *     offer_moment_reached), the server ALSO dedupes by a deterministic natural
 *     key. This keeps the API resilient when a retrying player REGENERATES the
 *     `eventId` for the same logical event (e.g. milestone 50 retried after a
 *     network blip with a fresh UUID). Without it, a single milestone could be
 *     counted twice and inflate retention.
 *
 * `eventNaturalKey()` returns the natural key for natural_key-class events and
 * `null` for event_id-class events (which must only dedupe by `eventId`).
 * ===================================================================================== */

export const IDEMPOTENCY_KEY_PREFIX = "nat";

/**
 * Derive the deterministic natural dedupe key for a natural_key-class event.
 * Returns `null` for event_id-class events.
 *
 * Milestone is keyed by its milestone value so 25/50/75/100 are each distinct
 * occurrences within one session. Every other natural_key event is keyed by
 * `{vslId, sessionId, type}`.
 */
export function eventNaturalKey(ev: AnalyticsEvent): string | null {
  const spec = ANALYTICS_EVENT_VOCABULARY[ev.type];
  if (!spec || spec.dedupe !== "natural_key") return null;
  const base = `${IDEMPOTENCY_KEY_PREFIX}:${ev.vslId}:${ev.sessionId}:${ev.type}`;
  if (ev.type === "milestone") {
    const m = ev.meta?.milestone ?? ev.value;
    return `${base}:${m ?? "unknown"}`;
  }
  return base;
}

/** Natural-key-class event types (the set the server dedupes beyond eventId). */
export const NATURAL_KEY_EVENT_TYPES: ReadonlySet<AnalyticsEventType> = new Set(
  (Object.values(ANALYTICS_EVENT_VOCABULARY) as AnalyticsEventSpec[])
    .filter((s) => s.dedupe === "natural_key")
    .map((s) => s.type),
);

/** True when this event type is deduped by natural key (not only eventId). */
export function isNaturalKeyEvent(ev: AnalyticsEvent): boolean {
  return NATURAL_KEY_EVENT_TYPES.has(ev.type);
}

/* =====================================================================================
 * §C. Retention milestone-bucket calculation rules
 *
 * Retention is computed on MAX-watched progress per visitor (never currentTime),
 * matching the CTA `basedOn: "maxWatched"` default and the anti-gaming rule:
 * rewinding cannot lower a visitor's retention contribution.
 *
 * Two canonical bucket grids, both derivable from the same per-visitor max pct:
 *  - `RETENTION_DECILES`:   0,10,20,…,100  (the overview curve, FUL-5).
 *  - `RETENTION_MILESTONES`: 0,25,50,75,100 (the milestone-bucket view, FUL-6).
 *
 * A visitor "reaches" a bucket when their max-watched pct ≥ the bucket cutoff.
 * `computeRetentionByBuckets()` is the single rule the dashboard (FUL-17) uses.
 * ===================================================================================== */

export const RETENTION_DECILES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;
export const RETENTION_MILESTONES = [0, 25, 50, 75, 100] as const;

/** Default grid used by the dashboard retention view. */
export const DEFAULT_RETENTION_GRID = RETENTION_DECILES;

export type RetentionGrid = readonly number[];

export interface RetentionBucket {
  /** Bucket cutoff, percent 0..100. */
  pct: number;
  /** Distinct visitors whose max-watched ≥ pct. */
  reached: number;
}

/** Largest bucket cutoff a visitor's max-watched pct satisfies. */
export function bucketizePct(maxWatchedPct: number, grid: RetentionGrid = DEFAULT_RETENTION_GRID): number {
  let bucket = 0;
  for (const cutoff of grid) {
    if (maxWatchedPct + 1e-9 >= cutoff) bucket = cutoff;
    else break;
  }
  return bucket;
}

/**
 * Compute retention buckets from a per-visitor max-watched map.
 *
 * `totalViewers` is the denominator for absolute counts; when omitted it is the
 * number of visitors in the map. Callers pass the overview's viewer set so
 * visitors with NO progress signal still count in the denominator at bucket 0
 * (they reached 0% by definition).
 */
export function computeRetentionByBuckets(
  maxPctByVisitor: Map<string, number>,
  options?: { grid?: RetentionGrid; totalViewers?: number },
): RetentionBucket[] {
  const grid = options?.grid ?? DEFAULT_RETENTION_GRID;
  const total = options?.totalViewers ?? maxPctByVisitor.size;
  return grid.map((pct) => {
    let reached = 0;
    for (const p of maxPctByVisitor.values()) if (p + 1e-9 >= pct) reached += 1;
    return { pct, reached: total === 0 && pct === 0 ? 0 : reached };
  });
}

/* =====================================================================================
 * §D. Conversion proxy
 *
 * The conversion proxy is the CTA click, measured against CTA shown. This is the
 * MVP conversion proxy per PRD §10.4 and the dashboard `ctaClickRate`. Central
 * aliasing lets FUL-17/dashboard code and the Full Funnel adapter (FUL-15)
 * reference a single canonical name.
 * ===================================================================================== */

/** The event type that represents a conversion in the MVP (PRD §10.4). */
export const CONVERSION_PROXY_EVENT: AnalyticsEventType = "cta_clicked";
/** The event type the conversion is measured against (CTA was seen). */
export const CONVERSION_DENOMINATOR_EVENT: AnalyticsEventType = "cta_shown";

/** A computed conversion-proxy metric for one VSL/window. */
export interface ConversionProxyMetric {
  vslId: ID;
  /** Numerator event count (cta_clicked). */
  conversions: number;
  /** Denominator event count (cta_shown). */
  opportunities: number;
  /** conversions / opportunities, rounded to 2dp; 0 when opportunities = 0. */
  conversionRate: number;
}

/** Round to 2dp (matches the contracts `pct` 2dp convention). */
function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Conversion-proxy rate from raw event counts.
 * `ctaClicked / ctaShown`, 0 when no CTA was shown (no divide-by-zero).
 */
export function conversionProxyRate(ctaShown: number, ctaClicked: number): number {
  if (!Number.isFinite(ctaShown) || ctaShown <= 0) return 0;
  return round2(ctaClicked / ctaShown);
}
